import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import useApi from "../../hooks/useApi";
import PageHeader from "./PageHeader";
import { RenderContext, ResourceConfig } from "./resources";
import { useAdminLang } from "./i18n";

interface ResourceListProps<T extends { id: number }> {
  config: ResourceConfig<T>;
}

export default function ResourceList<T extends { id: number }>({ config }: ResourceListProps<T>) {
  const { data, loading, error, reload } = useApi(config.list, [] as T[], [config.path]);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [deleteError, setDeleteError] = useState("");
  // fields changed after load (e.g. a message marked read) without reloading the table
  const [patches, setPatches] = useState<Record<number, Partial<T>>>({});
  const [viewingId, setViewingId] = useState<number | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const { t, lang } = useAdminLang();
  const ctx: RenderContext = { t, lang };

  const rows = useMemo(() => data.map((item) => ({ ...item, ...patches[item.id] }) as T), [data, patches]);
  const viewing = rows.find((item) => item.id === viewingId) ?? null;

  const openView = (item: T) => {
    setViewingId(item.id);
    config.view?.onOpen?.(item)
      .then((patch) => patch && setPatches((prev) => ({ ...prev, [item.id]: { ...prev[item.id], ...patch } })))
      .catch((err) => console.error(err));
  };

  // /admin/messages?view=12 (from the header bell) opens that item once the list is loaded
  const requestedId = Number(searchParams.get("view"));
  useEffect(() => {
    if (loading || !requestedId || !config.view) return;
    const item = rows.find((i) => i.id === requestedId);
    if (item) openView(item);
    setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, requestedId]);

  useEffect(() => {
    if (viewingId === null) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setViewingId(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [viewingId]);

  const handleDelete = async (id: number) => {
    if (!config.remove || !window.confirm(t("این مورد حذف شود؟"))) return;
    setDeletingId(id);
    setDeleteError("");
    try {
      await config.remove(id);
      if (viewingId === id) setViewingId(null);
      reload();
    } catch (err) {
      console.error(err);
      setDeleteError(t("حذف انجام نشد."));
    } finally {
      setDeletingId(null);
    }
  };

  const hasActions = !!config.view || !!config.update || !!config.remove;

  return (
    <>
      <PageHeader
        title={config.title}
        actions={
          config.create && (
            <Link to={`/admin/${config.path}/add`} className="btn btn-primary btn-wave">
              <i className="ri-add-line me-1 align-middle"></i>{t("افزودن")} {t(config.singular)}
            </Link>
          )
        }
      />

      <div className="card custom-card">
        <div className="card-header justify-content-between">
          <div className="card-title">
            <i className={`${config.icon} me-2 align-middle`}></i>{t("لیست")} {t(config.title)}
          </div>
          {!loading && !error && <span className="badge bg-primary-transparent">{data.length} {t("مورد")}</span>}
        </div>

        <div className="card-body p-0">
          {deleteError && <div className="alert alert-danger m-3 mb-0">{deleteError}</div>}

          {loading ? (
            <div className="text-center p-5">
              <div className="spinner-border text-primary" role="status"></div>
            </div>
          ) : error ? (
            <div className="text-center p-5">
              <p className="text-danger mb-3">{t("دریافت اطلاعات از سرور انجام نشد.")}</p>
              <button type="button" className="btn btn-sm btn-outline-primary" onClick={reload}>{t("تلاش دوباره")}</button>
            </div>
          ) : rows.length === 0 ? (
            <div className="text-center p-5 text-muted">{t(config.emptyText)}</div>
          ) : (
            <div className="table-responsive">
              <table className="table text-nowrap table-hover mb-0 align-middle">
                <thead>
                  <tr>
                    <th scope="col">#</th>
                    {config.columns.map((col) => (
                      <th scope="col" key={col.header}>{t(col.header)}</th>
                    ))}
                    {hasActions && <th scope="col" className="text-center">{t("عملیات")}</th>}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((item, index) => (
                    <tr key={item.id ?? index} className={config.rowClassName?.(item)}>
                      <td>{index + 1}</td>
                      {config.columns.map((col) => (
                        <td key={col.header} className={col.wrap ? "text-wrap admin-wrap-cell" : undefined}>{col.render(item, ctx)}</td>
                      ))}
                      {hasActions && (
                        <td className="text-center">
                          <div className="btn-list d-inline-flex gap-1">
                            {config.view && (
                              <button type="button" className="btn btn-sm btn-icon btn-primary-light" title={t("مشاهده")} onClick={() => openView(item)}>
                                <i className="ri-eye-line"></i>
                              </button>
                            )}
                            {config.update && (
                              <Link to={`/admin/${config.path}/edit/${item.id}`} className="btn btn-sm btn-icon btn-info-light" title={t("ویرایش")}>
                                <i className="ri-edit-line"></i>
                              </Link>
                            )}
                            {config.remove && config.canRemove?.(item) !== false && (
                              <button
                                type="button"
                                className="btn btn-sm btn-icon btn-danger-light"
                                title={t("حذف")}
                                disabled={deletingId === item.id}
                                onClick={() => handleDelete(item.id)}
                              >
                                {deletingId === item.id ? (
                                  <span className="spinner-border spinner-border-sm"></span>
                                ) : (
                                  <i className="ri-delete-bin-5-line"></i>
                                )}
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {config.view && viewing && (
        <>
          <div className="modal fade show d-block" tabIndex={-1} role="dialog" aria-modal="true" onClick={() => setViewingId(null)}>
            <div className="modal-dialog modal-dialog-centered modal-lg" onClick={(e) => e.stopPropagation()}>
              <div className="modal-content">
                <div className="modal-header">
                  <h6 className="modal-title">{config.view.title(viewing)}</h6>
                  <button type="button" className="btn-close" aria-label={t("بستن")} onClick={() => setViewingId(null)}></button>
                </div>
                <div className="modal-body">{config.view.render(viewing, ctx)}</div>
                <div className="modal-footer">
                  {config.view.footer?.(viewing, ctx)}
                  {config.remove && config.canRemove?.(viewing) !== false && (
                    <button type="button" className="btn btn-danger-light" disabled={deletingId === viewing.id} onClick={() => handleDelete(viewing.id)}>
                      <i className="ri-delete-bin-5-line me-1 align-middle"></i>{t("حذف")}
                    </button>
                  )}
                  <button type="button" className="btn btn-light" onClick={() => setViewingId(null)}>{t("بستن")}</button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      )}
    </>
  );
}
